import { DateTime } from "luxon";

/**
 * CMS Federal Fiscal Year mapping. FY Y starts Oct 1 of (Y-1).
 *
 * | FY label | Calendar window               |
 * | -------- | ----------------------------- |
 * | FY Y Q1  | (Y-1)-10-01 → (Y-1)-12-31     |
 * | FY Y Q2  | Y-01-01    → Y-03-31          |
 * | FY Y Q3  | Y-04-01    → Y-06-30          |
 * | FY Y Q4  | Y-07-01    → Y-09-30          |
 *
 * Mirrors `quarter_window` in `backend/services/pbj_model/__init__.py` so the
 * FE clamp/range logic stays in lock-step with the BE data window.
 */
export const fyQuarterRange = (year: number, quarter: number): { start: DateTime; end: DateTime } => {
	const startYear = quarter === 1 ? year - 1 : year;
	const startMonth = quarter === 1 ? 10 : (quarter - 2) * 3 + 1;
	const start = DateTime.fromObject({ year: startYear, month: startMonth, day: 1 });
	return { start, end: start.plus({ months: 3 }).minus({ days: 1 }) };
};

export const currentFyQuarter = (today: DateTime = DateTime.now()): { year: number; quarter: number } => {
	if (today.month >= 10) return { year: today.year + 1, quarter: 1 };
	return { year: today.year, quarter: Math.floor((today.month - 1) / 3) + 2 };
};

/**
 * "Current quarter to date" — start of the in-progress FY quarter through today.
 * Used for the quick-pick chip and as the page default so admins land on the
 * window they're currently accruing toward (vs. the last *completed* quarter).
 */
export const currentFyQuarterToDateRange = (today: DateTime = DateTime.now()): { start: DateTime; end: DateTime } => {
	const { year, quarter } = currentFyQuarter(today);
	const { start } = fyQuarterRange(year, quarter);
	return { start, end: today.startOf("day") };
};

export const previousFyQuarter = (today: DateTime = DateTime.now()): { year: number; quarter: number } => {
	const { year, quarter } = currentFyQuarter(today);
	return quarter === 1 ? { year: year - 1, quarter: 4 } : { year, quarter: quarter - 1 };
};

/**
 * Last `n` FY quarters in *descending* order — i.e. the most recently completed
 * quarter first. Used to render the quick-pick preset chips on the PBJ period
 * selector (default page lands on `[0]`).
 */
export const lastNFyQuarters = (today: DateTime, n: number): Array<{ year: number; quarter: number }> => {
	const presets: Array<{ year: number; quarter: number }> = [];
	let cursor = previousFyQuarter(today);
	for (let i = 0; i < n; i++) {
		presets.push(cursor);
		// Walk back one quarter per iteration; FY Q1 of year Y rolls back to FY Q4 of Y-1.
		cursor = cursor.quarter === 1 ? { year: cursor.year - 1, quarter: 4 } : { year: cursor.year, quarter: cursor.quarter - 1 };
	}
	return presets;
};
